import React, { useState, useEffect } from 'react';
import { salesApi } from '../../api';
import { PageHeader, Table, Pagination, Modal, FormItem, message, confirm, formatDate } from '../../components/common';

const SalesRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [filters, setFilters] = useState({ is_delivered: '', is_settled: '', keyword: '' });
  const [sort, setSort] = useState({ field: 'created_at', order: 'desc' });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '', customer_gender: '', customer_age: '', customer_phone: '', customer_email: '', amount: '', remark: ''
  });
  const amountOptions = [149, 150, 100, 80, 50];

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters,
        sort_by: sort.field,
        order: sort.order
      };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await salesApi.getList(params);
      setRecords(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, [pagination.page, pagination.per_page, sort]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchRecords();
  };

  const handleSort = (field, order) => {
    setSort({ field, order });
  };

  const handleCreate = () => {
    setEditingRecord(null);
    setFormData({ customer_name: '', customer_gender: '', customer_age: '', customer_phone: '', customer_email: '', amount: '', remark: '' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    if (!confirm(`确定删除该销售记录吗？`)) return;
    try {
      await salesApi.delete(record.id);
      message.success('删除成功');
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingRecord) {
        await salesApi.update(editingRecord.id, formData);
        message.success('更新成功');
      } else {
        await salesApi.create(formData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDeliver = async (record) => {
    try {
      await salesApi.markDelivered(record.id);
      message.success('已标记为发号');
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleSettle = async (record) => {
    try {
      await salesApi.markSettled(record.id);
      message.success('已标记为结算');
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    { key: 'id', title: 'ID', width: '60px', sorter: true },
    { key: 'customer_name', title: '客户名称', render: (v) => v || '-', sorter: true },
    { key: 'customer_email', title: '客户邮箱', sorter: true },
    { key: 'customer_phone', title: '手机号', render: (v) => v || '-' },
    { key: 'customer_gender', title: '性别', render: (v) => v === 'male' ? '男' : v === 'female' ? '女' : '-' },
    { key: 'customer_age', title: '年龄', render: (v) => v || '-' },
    { key: 'amount', title: '金额', render: (v) => `¥${v.toFixed(2)}`, sorter: true },
    { key: 'google_account', title: '绑定账号', render: (v) => v ? v.email : <span className="tag tag-warning">待绑定</span> },
    { key: 'is_delivered', title: '发号状态', render: (v) => <span className={`tag ${v ? 'tag-success' : 'tag-default'}`}>{v ? '已发号' : '未发号'}</span>, sorter: true },
    { key: 'is_settled', title: '结算状态', render: (v) => <span className={`tag ${v ? 'tag-success' : 'tag-default'}`}>{v ? '已结算' : '待结算'}</span>, sorter: true },
    { key: 'created_at', title: '创建时间', render: (v) => formatDate(v), sorter: true },
    {
      key: 'actions', title: '操作', render: (_, row) => (
        <div className="action-buttons">
          <button className="btn btn-small btn-default" onClick={() => handleEdit(row)}>编辑</button>
          {row.google_account_id && !row.is_delivered && (
            <button className="btn btn-small btn-success" onClick={() => handleDeliver(row)}>发号</button>
          )}
          {!row.is_settled && (
            <button className="btn btn-small btn-success" onClick={() => handleSettle(row)}>结算</button>
          )}
          <button className="btn btn-small btn-danger" onClick={() => handleDelete(row)}>删除</button>
        </div>
      )
    }
  ];

  return (
    <div>
      <PageHeader title="我的销售记录" extra={<button className="btn btn-primary" onClick={handleCreate}>新建记录</button>} />

      <div className="search-bar">
        <div className="form-item">
          <input placeholder="搜索客户名称" value={filters.keyword} onChange={e => setFilters({ ...filters, keyword: e.target.value })} />
        </div>
        <div className="form-item">
          <select value={filters.is_delivered} onChange={e => setFilters({ ...filters, is_delivered: e.target.value })}>
            <option value="">全部发号状态</option>
            <option value="true">已发号</option>
            <option value="false">未发号</option>
          </select>
        </div>
        <div className="form-item">
          <select value={filters.is_settled} onChange={e => setFilters({ ...filters, is_settled: e.target.value })}>
            <option value="">全部结算状态</option>
            <option value="true">已结算</option>
            <option value="false">待结算</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={handleSearch}>搜索</button>
      </div>

      <Table
        columns={columns}
        data={records}
        loading={loading}
        sort={sort}
        onSort={handleSort}
      />
      <Pagination current={pagination.page} total={pagination.total} pageSize={pagination.per_page}
        onChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(size) => setPagination(prev => ({ ...prev, per_page: size, page: 1 }))} />

      <Modal visible={modalVisible} title={editingRecord ? '编辑销售记录' : '新建销售记录'} onClose={() => setModalVisible(false)}
        footer={<><button className="btn btn-default" onClick={() => setModalVisible(false)}>取消</button>
          <button className="btn btn-primary" onClick={handleSubmit}>确定</button></>}>
        <FormItem label="客户名称">
          <input value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} placeholder="选填" />
        </FormItem>
        <FormItem label="客户邮箱" required>
          <input type="email" value={formData.customer_email || ''} onChange={e => setFormData({ ...formData, customer_email: e.target.value })} placeholder="必填" />
        </FormItem>
        <FormItem label="客户手机号">
          <input value={formData.customer_phone || ''} onChange={e => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="选填" />
        </FormItem>
        <FormItem label="客户性别">
          <select value={formData.customer_gender || ''} onChange={e => setFormData({ ...formData, customer_gender: e.target.value })}>
            <option value="">请选择</option>
            <option value="male">男</option>
            <option value="female">女</option>
          </select>
        </FormItem>
        <FormItem label="客户年龄">
          <input type="number" value={formData.customer_age || ''} onChange={e => setFormData({ ...formData, customer_age: e.target.value ? parseInt(e.target.value) : '' })} placeholder="选填" />
        </FormItem>
        <FormItem label="销售金额" required>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) || '' })} style={{ width: '120px' }} placeholder="输入金额" />
            <span style={{ color: '#666' }}>或选择：</span>
            {amountOptions.map(opt => (
              <button key={opt} type="button" className={`btn btn-small ${formData.amount === opt ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setFormData({ ...formData, amount: opt })}>¥{opt}</button>
            ))}
          </div>
        </FormItem>
        <FormItem label="备注">
          <textarea value={formData.remark || ''} onChange={e => setFormData({ ...formData, remark: e.target.value })} />
        </FormItem>
      </Modal>
    </div>
  );
};

export default SalesRecords;
